import { useModalStatusMessage } from '@/hooks';
import { useSelector } from '@/store';
import { useMutation, useQuery } from '@apollo/client';
import ModalActions from '@components/modal/ModalActions';
import ModalTitle from '@components/modal/ModalTitle';
import DeviceTooltip from '@components/tooltips/DeviceTooltip';
import { RETRY_DGA_REPORTS } from '@features/dga/graphql/mutations/retryReportsForDevice';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Dialog, DialogContent, Skeleton, TextField, Typography } from '@mui/material';
import { type FC, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import { setRetryReportsModal } from '../../slices/dgaSlice';
import { useForm, useWatch } from 'react-hook-form';
import { TriggerRetryReportsInput } from '@/__generated__/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import { getTriggerRetryReportsMutationSchema } from '@features/dga/utils/validationSchemas';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers';
import { USE_TIME_RANGE_ON_TRIGGER_RETRY_DGA_REPORTS } from '@/graphql/querys/access/getAccessUseTimeRangeOnTriggerRetryDGAReports';

interface Props {
  onClose: () => void;
  deviceId: string;
}

type FormValues = TriggerRetryReportsInput;

const Component: FC<Props> = ({ deviceId, onClose }) => {
  const { data: access } = useQuery(USE_TIME_RANGE_ON_TRIGGER_RETRY_DGA_REPORTS, {
    fetchPolicy: 'network-only'
  })
  const [retryReportsForDevice, { loading }] = useMutation(RETRY_DGA_REPORTS, {
    onCompleted() {
      openModalSuccessMessage(_(msg`Se realizará un nuevo intento de envío de los datos. Se sugiere verificar el estado de los envíos nuevamente pasadas las dos horas desde el reintento.`));
      onClose()
    },
    onError(error) {
      openModalErrorMessage(_(msg`Hubo un error al republicar los datos`) + ': ' + error.message);
    },
  });

  const { _, i18n } = useLingui();

  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const methods = useForm<FormValues>({
    resolver: zodResolver(getTriggerRetryReportsMutationSchema(i18n)),
    defaultValues: {
      deviceId,
    },
  })

  const onSubmit = async (values: FormValues) => {
    retryReportsForDevice({
      variables: {
        input: values
      },
    });
  };

  const startTimestamp = useWatch({
    control: methods.control,
    name: 'startTimestamp',
    exact: true,
  });

  const stopTimestamp = useWatch({
    control: methods.control,
    name: 'stopTimestamp',
    exact: true,
  });

  const errors = methods.formState.errors

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)}>
      <ModalTitle onClose={onClose} title={_(msg`Reintentar publicación de reportes fallidos`)} />
      <DialogContent dividers>
        <Typography>
          <Trans>
            <Typography variant='h6'>
              El envío de datos no se realiza de forma inmediata y puede presentar demoras, dependiendo de la cantidad de envíos pendientes y la disponibilidad del servicio.
            </Typography>
            <Typography mt={2}>
              ¿Quieres reintentar la publicación de todos los reportes fallidos del{' '}
            </Typography>
            <DeviceTooltip deviceId={deviceId}>
              <span
                style={{
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  textDecorationColor: 'divider',
                }}
              >
                dispositivo con id {deviceId}
              </span>
            </DeviceTooltip>
            ?
          </Trans>
        </Typography>
        {access?.authorization.authorizationGeneral?.useTimeRangeOnTriggerRetryDGAReports && (
          <Box mt={4}>
            <Box display="flex" mt={1}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box mr={1} width="100%">
                  <DateTimePicker
                    renderInput={(params) => <TextField {...params} label={_(msg`Inicio`)} fullWidth />}
                    value={startTimestamp ? dayjs.unix(startTimestamp).startOf('hour').valueOf() : null}
                    onChange={(value: Dayjs | null) => {
                      methods.setValue(
                        `startTimestamp`,
                        value?.startOf('hour')?.unix() ?? null,
                        { shouldDirty: true },
                      );
                    }}
                    minDate={dayjs().subtract(6, 'months').startOf('month')}
                    maxDate={dayjs().startOf('hour')}
                  />
                </Box>
                <Box width="100%">
                  <DateTimePicker
                    renderInput={(params) => <TextField {...params} label={_(msg`Final`)} fullWidth />}
                    value={stopTimestamp ? dayjs.unix(stopTimestamp).startOf('hour').valueOf() : null}
                    onChange={(value: Dayjs | null) => {
                      methods.setValue(
                        `stopTimestamp`,
                        value?.startOf('hour')?.unix() ?? null,
                        { shouldDirty: true },
                      );
                    }}
                    minDate={dayjs().subtract(6, 'months').startOf('month')}
                    maxDate={dayjs().startOf('hour')}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
            {errors?.deviceId?.message && <Typography color="error" fontStyle="italic">{errors?.deviceId?.message}</Typography>}
            <Typography fontStyle="italic">
              Si no selecciona ningún rango, se procesaran los datos de los últimos 7 días.
            </Typography>
            <Typography fontStyle="italic">
              (Debe seleccionar ambos o ningúno)
            </Typography>
          </Box>
        )}
      </DialogContent>
      <ModalActions
        onClose={onClose}
        onSubmit="submit"
        submitLabel={_(msg`Confirmar`)}
        submitLoading={loading}
      />
    </form>
  );
};

const Fallback: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { _ } = useLingui();
  return (
    <>
      <ModalTitle onClose={onClose} title={_(msg`Reintentar publicación de reportes fallidos`)} />
      <DialogContent dividers>
        <Skeleton variant="rounded" height={200} />
      </DialogContent>
      <ModalActions onClose={onClose} />
    </>
  );
};

const DGAReportsRetryModal: FC = () => {
  const dispatch = useDispatch();
  const { retryReportsModal } = useSelector((state) => state.dga_store);

  const onClose = () => {
    dispatch(
      setRetryReportsModal({
        open: false,
      }),
    );
  };

  return (
    <Dialog onClose={onClose} open={retryReportsModal.open} maxWidth="sm" fullWidth>
      <Suspense fallback={<Fallback onClose={onClose} />}>
        {retryReportsModal.open && (
          <Component onClose={onClose} deviceId={retryReportsModal.deviceId} />
        )}
      </Suspense>
    </Dialog>
  );
};

export default DGAReportsRetryModal;
